<template>
  <BaseMap class="h-full" @map-loaded="mapLoaded" :id="props.id" />
</template>

<script setup>
import { defineProps, defineEmits, onUnmounted, watch } from "vue";
import * as turf from "@turf/turf";
import { calculateOpacity } from "@/view/ancestry/recent/mapHelpers.js";
import { useRoute, useRouter } from "vue-router";
import BaseMap from "@/components/Maps/BaseMap.vue";

const genderColors = {
  fatherline: "#00A5B0",
  motherline: "#d40b55",
};
const color = (gender) => genderColors[gender];

const props = defineProps({
  coverageData: Object,
  gender: String,
  id: { type: String, default: "map" },
  autoFit: { type: Boolean, default: false },
});

const emits = defineEmits(["update:modelValue"]);
const route = useRoute();
const router = useRouter();

let mapInstance = null;

function fitMapToCoverageData(map) {
  try {
    if (!props?.coverageData?.data) return;
    const bbox = turf.bbox(props.coverageData.data);
    if (!bbox || bbox.length !== 4) return;
    const height = map.getContainer()?.offsetHeight || 250;
    const padding = height <= 300 ? 20 : 80;
    map.fitBounds(bbox, { padding, animate: false, maxZoom: 6, duration: 0 });
  } catch (e) { console.error(e); }
}

function createActiveLayer(map, featureId) {
  if (map.getLayer("activeLayer")) map.removeLayer("activeLayer");
  map.addLayer({
    id: "activeLayer",
    type: "line",
    source: featureId,
    paint: { "line-color": color(props.gender), "line-width": 2 },
  });
  const feat = props.coverageData.data.features.find(f => f.id === featureId);
  if (feat) map.fitBounds(turf.bbox(feat.geometry), { padding: 50 });
}

function mapLoaded(map) {
  mapInstance = map;
  if (!props?.coverageData?.data?.features) {
    // No data – just show base map
    return;
  }
  
  const container = map.getContainer();
  if ("ResizeObserver" in window) {
    const ro = new ResizeObserver(() => map.resize());
    ro.observe(container);
    map._resizeObserver = ro;
  }

  // Add layers
  props.coverageData.data.features.forEach(feature => {
    if (!feature?.id || !feature?.geometry) return;
    map.addSource(feature.id, {
      type: "geojson",
      data: { type: "Feature", geometry: feature.geometry },
    });
    map.addLayer({
      id: feature.id,
      type: "fill",
      source: feature.id,
      paint: {
        "fill-color": color(props.gender),
        "fill-opacity": calculateOpacity(feature.properties?.percentage || 0),
      },
    });
    map.on("click", feature.id, handleFeatureClick(feature.id));
  });

  // ---- Decide view ----
  if (props.autoFit) {
    // Card view: world overview
    map.setCenter([0, 20]);
    map.setZoom(0.45);
  } else {
    // Full page: fit to data
    fitMapToCoverageData(map);
    setupFullPageMode(map);
  }
}

function handleFeatureClick(featureId) {
  return ({ features }) => {
    const [f] = features || [];
    if (!f) return;
    emits("update:modelValue", featureId);
    router.push({
      name: `${props.gender}-distribution-map`,
      params: { region: f.source || featureId },
    });
  };
}

function setupFullPageMode(map) {
  if (map._regionWatcher) {
    map._regionWatcher();
    map._regionWatcher = null;
  }
  const watcher = watch(
    () => route.params.region,
    (newRegion) => {
      try {
        if (newRegion) createActiveLayer(map, newRegion);
        else fitMapToCoverageData(map);
      } catch (e) { console.error(e); }
    },
    { immediate: true }
  );
  map._regionWatcher = watcher;
}

onUnmounted(() => {
  if (mapInstance?._resizeObserver) {
    mapInstance._resizeObserver.disconnect();
    mapInstance._resizeObserver = null;
  }
  if (typeof mapInstance?._regionWatcher === 'function') {
    mapInstance._regionWatcher();
    mapInstance._regionWatcher = null;
  }
});
</script>